import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Zap, Star, ArrowRight } from 'lucide-react';
import { plans, companyInfo } from '../data/mock';

const PlanCard = ({ plan, index, isPopular }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative group"
    >
      {/* Glow Effect */}
      <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${plan.color} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500`} />
      
      <div className={`relative h-full rounded-3xl p-6 transition-all duration-500 ${
        isPopular 
          ? 'bg-gradient-to-br from-[#0f1f3a] to-[#0a1628] border-2 border-blue-500/50' 
          : 'bg-[#0a1628]/80 border border-white/10 hover:border-white/20'
      }`}>
        {/* Popular Badge */}
        {isPopular && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-4 left-1/2 -translate-x-1/2"
          >
            <div className="px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-sm font-bold flex items-center gap-1 shadow-lg shadow-blue-500/30">
              <Star className="w-4 h-4" fill="currentColor" />
              Mais Popular
            </div>
          </motion.div>
        )}

        {/* Plan Name */}
        <div className="text-center mb-6">
          <p className="text-xs text-cyan-400 uppercase tracking-widest mb-2">GFA Fibra</p>
          <h3 className="text-xl font-bold text-white">{plan.name}</h3>
        </div>

        {/* Speed Display */}
        <div className="text-center py-6 mb-6">
          <motion.div
            animate={isHovered ? { scale: 1.05 } : { scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <span className={`text-6xl font-bold bg-gradient-to-r ${plan.color} bg-clip-text text-transparent`}>
              {plan.speed}
            </span>
            <span className="text-xl font-semibold text-gray-500 ml-2">{plan.unit}</span>
          </motion.div>
        </div>

        {/* Features */}
        <div className="space-y-3 mb-6">
          {plan.features.map((feature, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + idx * 0.05 }}
              className="flex items-center gap-3"
            >
              <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-green-400" />
              </div>
              <span className="text-gray-400 text-sm">{feature}</span>
            </motion.div>
          ))}
        </div>

        {/* Price */}
        <div className="text-center mb-6 pt-4 border-t border-white/10">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-sm text-gray-500">R$</span>
            <span className="text-4xl font-bold text-white">{Math.floor(plan.price)}</span>
            <span className="text-xl text-gray-400">,{String(plan.price).split('.')[1] || '00'}</span>
            <span className="text-gray-500 text-sm">/mês</span>
          </div>
        </div>

        {/* CTA Button */}
        <motion.button
          onClick={() => window.open(
            `https://wa.me/${companyInfo.whatsapp}?text=Olá! Tenho interesse no plano ${plan.name} ${plan.speed} ${plan.unit}`,
            '_blank'
          )}
          className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 flex items-center justify-center gap-2 group/btn ${
            isPopular
              ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40'
              : 'bg-white/5 text-white border border-white/20 hover:bg-white/10 hover:border-white/40'
          }`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Zap className="w-5 h-5" />
          <span>Assinar Agora</span>
          <ArrowRight className="w-4 h-4 opacity-0 group-hover/btn:opacity-100 group-hover/btn:translate-x-1 transition-all" />
        </motion.button>
      </div>
    </motion.div>
  );
};

const PlansSection = ({ selectedCity }) => {
  return (
    <section id="planos" className="relative py-24 bg-[#0a1628] overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[128px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 text-cyan-400 text-sm font-medium mb-6"
          >
            Nossos Planos
          </motion.span>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Escolha sua{' '}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Velocidade
            </span>
          </h2>
          
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            {selectedCity
              ? `Planos disponíveis em ${selectedCity.name}. Fibra óptica de alta performance.`
              : 'Selecione sua cidade para ver os planos disponíveis.'}
          </p>
        </motion.div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, index) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              index={index}
              isPopular={plan.popular}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="text-gray-500 mb-4">
            Não encontrou o plano ideal? Fale com nossos consultores!
          </p>
          <motion.button
            onClick={() => window.open(
              `https://wa.me/${companyInfo.whatsapp}?text=Olá! Gostaria de mais informações sobre os planos.`,
              '_blank'
            )}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/20 text-gray-300 hover:text-white hover:border-white/40 hover:bg-white/5 transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Falar com Consultor
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default PlansSection;
